import React, { useState, useMemo } from 'react';
import * as Icons from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { PREDEFINED_HABITS, HABIT_ICONS, HABIT_COLORS } from '../lib/constants';
import { DAYS_OF_WEEK } from '../lib/avatars';
import { addHabit } from '../lib/storage';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';
import { Filter, X } from 'lucide-react';

const AddHabitModal = ({ open, onClose, onHabitAdded }) => {
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customHabit, setCustomHabit] = useState({
    name: '',
    icon: 'Circle',
    category: '',
    color: HABIT_COLORS[0],
    scheduledDays: [], // Empty = daily
  });

  // Extract unique categories from predefined habits
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(PREDEFINED_HABITS.map(h => h.category))];
    return uniqueCategories.sort();
  }, []);

  // Filter habits by category
  const filteredHabits = useMemo(() => {
    if (!selectedCategory) return PREDEFINED_HABITS;
    return PREDEFINED_HABITS.filter(h => h.category === selectedCategory);
  }, [selectedCategory]);

  const toggleDay = (day) => {
    setCustomHabit(prev => {
      const days = prev.scheduledDays || [];
      if (days.includes(day)) {
        return { ...prev, scheduledDays: days.filter(d => d !== day) };
      } else {
        return { ...prev, scheduledDays: [...days, day] };
      }
    });
  };

  const handleTemplateSelect = async (template) => {
    if (loading) return;
    
    try {
      setLoading(true);
      const newHabit = await addHabit({
        name: template.name,
        icon: template.icon,
        category: template.category,
        color: template.color,
        scheduledDays: null, // Templates default to daily
      });
      toast.success(`Added "${template.name}" habit!`);
      onClose();
      // Call onHabitAdded after closing to ensure modal closes first
      setTimeout(() => onHabitAdded(newHabit), 0);
    } catch (error) {
      console.error('Error adding habit:', error);
      toast.error('Failed to add habit');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    if (!customHabit.name.trim()) {
      toast.error('Please enter a habit name');
      return;
    }

    if (loading) return;

    try {
      setLoading(true);
      // If all days selected or none selected, set to null (daily)
      const scheduledDays = customHabit.scheduledDays.length === 0 || customHabit.scheduledDays.length === 7 
        ? null 
        : customHabit.scheduledDays;
      
      const newHabit = await addHabit({
        ...customHabit,
        scheduledDays,
      });
      toast.success(`Added "${customHabit.name}" habit!`);
      setCustomHabit({
        name: '',
        icon: 'Circle',
        category: '',
        color: HABIT_COLORS[0],
        scheduledDays: [],
      });
      onClose();
      // Call onHabitAdded after closing to ensure modal closes first
      setTimeout(() => onHabitAdded(newHabit), 0);
    } catch (error) {
      console.error('Error adding habit:', error);
      toast.error('Failed to add habit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-2xl max-h-[90vh] overflow-hidden gradient-card"
        data-testid="add-habit-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl gradient-text">Add New Habit</DialogTitle>
          <DialogDescription>
            Choose from predefined templates or create your own custom habit.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-1 rounded-xl" data-testid="habit-tabs">
            <TabsTrigger 
              value="templates" 
              data-testid="tab-templates"
              className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white"
            >
              Templates
            </TabsTrigger>
            <TabsTrigger 
              value="custom" 
              data-testid="tab-custom"
              className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white"
            >
              Custom
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-4">
            {/* Category Filter Chips */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filter by category:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="category-all"
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    !selectedCategory
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  All ({PREDEFINED_HABITS.length})
                </button>
                {categories.map((category) => {
                  const count = PREDEFINED_HABITS.filter(h => h.category === category).length;
                  const isSelected = selectedCategory === category;
                  return (
                    <button
                      key={category}
                      type="button"
                      data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setSelectedCategory(isSelected ? null : category)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                        isSelected
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {category} ({count})
                      {isSelected && <X className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <ScrollArea className="h-[380px] pr-4">
              {filteredHabits.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  No habits found in this category
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredHabits.map((template, index) => {
                    const IconComponent = Icons[template.icon] || Icons.Circle;
                    return (
                      <button
                        key={index}
                        data-testid={`template-${template.name.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => handleTemplateSelect(template)}
                        disabled={loading}
                        className="flex items-start gap-3 p-4 text-left gradient-card rounded-xl border border-white/20 dark:border-white/10 hover:shadow-lg hover-glow transition-all duration-200 disabled:opacity-50"
                      >
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
                          style={{ 
                            background: `linear-gradient(135deg, ${template.color}40, ${template.color}80)` 
                          }}
                        >
                          <IconComponent className="w-5 h-5" style={{ color: template.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-1">
                            {template.name}
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                            {template.description}
                          </p>
                          <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                            {template.category}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="custom" className="mt-4">
            <ScrollArea className="h-[450px] pr-4">
              <form onSubmit={handleCustomSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="habit-name">Habit Name *</Label>
                  <Input
                    id="habit-name"
                    data-testid="custom-habit-name-input"
                    placeholder="e.g., Practice Piano"
                    value={customHabit.name}
                    onChange={(e) => setCustomHabit({ ...customHabit, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="habit-category">Category (Optional)</Label>
                  <Input
                    id="habit-category"
                    data-testid="custom-habit-category-input"
                    placeholder="e.g., Music, Health, Learning"
                    value={customHabit.category}
                    onChange={(e) => setCustomHabit({ ...customHabit, category: e.target.value })}
                  />
                </div>

                {/* Schedule Days */}
                <div className="space-y-2">
                  <Label>Schedule (leave empty for daily)</Label>
                  <div className="flex gap-2 flex-wrap">
                    {DAYS_OF_WEEK.map((day) => {
                      const isSelected = customHabit.scheduledDays?.includes(day.short);
                      return (
                        <button
                          key={day.short}
                          type="button"
                          data-testid={`day-${day.short}`}
                          onClick={() => toggleDay(day.short)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isSelected
                              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          {day.short}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500">
                    {customHabit.scheduledDays?.length === 0 
                      ? 'Daily habit' 
                      : customHabit.scheduledDays?.length === 7 
                        ? 'Daily habit'
                        : `Every ${customHabit.scheduledDays?.join(', ')}`}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="grid grid-cols-10 gap-2 max-h-48 overflow-y-auto p-2 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                    {HABIT_ICONS.map((iconName) => {
                      const IconComponent = Icons[iconName] || Icons.Circle;
                      const isSelected = customHabit.icon === iconName;
                      return (
                        <button
                          key={iconName}
                          type="button"
                          data-testid={`icon-${iconName}`}
                          onClick={() => setCustomHabit({ ...customHabit, icon: iconName })}
                          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                            isSelected
                              ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg scale-110'
                              : 'bg-white dark:bg-slate-800 hover:bg-gradient-to-br hover:from-blue-500/20 hover:to-purple-600/20 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
                          }`}
                          title={iconName}
                        >
                          <IconComponent className="w-5 h-5" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2 flex-wrap">
                    {HABIT_COLORS.map((color) => {
                      const isSelected = customHabit.color === color;
                      return (
                        <button
                          key={color}
                          type="button"
                          data-testid={`color-${color}`}
                          onClick={() => setCustomHabit({ ...customHabit, color })}
                          className={`w-10 h-10 rounded-lg transition-all duration-200 ${
                            isSelected ? 'ring-4 ring-blue-500 ring-offset-2 scale-110' : 'hover:scale-110'
                          }`}
                          style={{ backgroundColor: color }}
                          aria-label={`Select color ${color}`}
                        />
                      );
                    })}
                  </div>
                </div>

                <DialogFooter className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    data-testid="cancel-habit-button"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="create-habit-button"
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover-glow"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Habit'}
                  </Button>
                </DialogFooter>
              </form>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddHabitModal;
